import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Badge,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { requestApi, UserRequest, eventApi, chatApi, ChatThread, ThreadParticipant } from '../utils/api';
import { Event } from '../types/event';
import { useNavigate } from 'react-router-dom';
import { getUserInitials } from '../utils/userUtils';

interface InboxRequest extends UserRequest {
  event?: Event;
}

const Inbox: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);

  // Requests state
  const [requests, setRequests] = useState<InboxRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Chats state
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [participants, setParticipants] = useState<{ [threadId: string]: ThreadParticipant[] }>({});
  const [chatsLoading, setChatsLoading] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      loadInboxRequests();
      if (tabValue === 1) {
        loadChats();
      }
    }
  }, [isAuthenticated, tabValue]);

  const loadInboxRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get all requests and filter for ones where current user is the host
      const allRequests = await requestApi.getUserRequests();
      const hostRequests = allRequests.filter(request =>
        request.host_id === user.id && request.status !== 'ACCEPTED'
      );

      // Get event details for each request
      const requestsWithEvents = await Promise.all(
        hostRequests.map(async (request) => {
          try {
            const event = await eventApi.getEventById(request.event_id);
            return { ...request, event };
          } catch (err) {
            console.warn(`Failed to fetch event ${request.event_id}:`, err);
            return request;
          }
        })
      );

      // Sort by created date (newest first)
      requestsWithEvents.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setRequests(requestsWithEvents);
      setError(null);
    } catch (err) {
      setError('Failed to load inbox requests');
      console.error('Error loading inbox requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setActionLoading(requestId);
      await requestApi.acceptRequest(requestId);

      // Update local state
      setRequests(prev => prev.map(req =>
        req.id === requestId ? { ...req, status: 'ACCEPTED' as any } : req
      ));

      setError(null);
    } catch (err: any) {
      console.error('Error accepting request:', err);
      if (err.response?.status === 400) {
        setError('Request has already been processed');
      } else if (err.response?.status === 403) {
        setError('You are not authorized to accept this request');
      } else {
        setError('Failed to accept request');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineRequest = async (requestId: string) => {
    try {
      setActionLoading(requestId);
      await requestApi.declineRequest(requestId);

      // Update local state
      setRequests(prev => prev.map(req =>
        req.id === requestId ? { ...req, status: 'DECLINED' as any } : req
      ));

      setError(null);
    } catch (err: any) {
      console.error('Error declining request:', err);
      if (err.response?.status === 400) {
        setError('Request has already been processed');
      } else if (err.response?.status === 403) {
        setError('You are not authorized to decline this request');
      } else {
        setError('Failed to decline request');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const loadChats = async () => {
    try {
      setChatsLoading(true);
      const response = await chatApi.getThreads();
      setThreads(response.threads);
      setParticipants(response.participants);

      // Calculate total unread count
      const unreadCount = response.threads.reduce((total, thread) => total + thread.unread_count, 0);
      setTotalUnreadCount(unreadCount);
    } catch (err) {
      console.error('Error loading chats:', err);
      setError('Failed to load chats');
    } finally {
      setChatsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChatClick = (threadId: string) => {
    navigate(`/chat?thread=${threadId}`);
  };

  const formatChatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return '#ff9800';
      case 'ACCEPTED': return '#4caf50';
      case 'DECLINED': return '#f44336';
      case 'EXPIRED': return '#9e9e9e';
      default: return '#533B7D';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return 'Pending';
      case 'ACCEPTED': return 'Accepted';
      case 'DECLINED': return 'Declined';
      case 'EXPIRED': return 'Expired';
      default: return status;
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please log in to view your inbox.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <InboxIcon sx={{ color: '#533B7D', fontSize: { xs: 24, sm: 28, md: 32 } }} />
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#333',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
          }}>
            Inbox
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          Manage join requests and chat with event participants
        </Typography>

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ mt: 3, mb: 2 }}
          indicatorColor="primary"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InboxIcon />
                <span>Requests</span>
                {requests.filter(r => r.status === 'SUBMITTED').length > 0 && (
                  <Badge
                    badgeContent={requests.filter(r => r.status === 'SUBMITTED').length}
                    color="error"
                  />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChatIcon />
                <span>Chats</span>
                {totalUnreadCount > 0 && (
                  <Badge badgeContent={totalUnreadCount} color="error" />
                )}
              </Box>
            }
          />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Content */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
        {/* Requests Tab */}
        {tabValue === 0 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#533B7D' }} />
              </Box>
            ) : requests.length > 0 ? (
              <Grid container spacing={3}>
                {requests.map((request) => (
                  <Grid item xs={12} key={request.id}>
                    <Card
                      elevation={1}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          boxShadow: 2
                        }
                      }}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 } }}>
                          {/* User Avatar - clickable to profile */}
                          <Box
                            onClick={() => {
                              if (user && request.user_id === user.id) {
                                navigate('/profile');
                              } else {
                                navigate(`/user/${request.user_id}`);
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              flexShrink: 0,
                              '&:hover': { opacity: 0.85 }
                            }}
                          >
                            <Avatar sx={{ 
                              backgroundColor: '#533B7D', 
                              width: { xs: 40, sm: 48 }, 
                              height: { xs: 40, sm: 48 }, 
                              fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                              {getUserInitials(request.user_name || 'User')}
                            </Avatar>
                          </Box>

                          {/* Request Content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: { xs: 1, sm: 2 }, 
                              mb: 1,
                              flexWrap: 'wrap'
                            }}>
                              <Typography
                                variant="h6"
                                onClick={() => {
                                  if (user && request.user_id === user.id) {
                                    navigate('/profile');
                                  } else {
                                    navigate(`/user/${request.user_id}`);
                                  }
                                }}
                                sx={{ 
                                  fontWeight: 'bold',
                                  fontSize: { xs: '1rem', sm: '1.25rem' },
                                  cursor: 'pointer',
                                  '&:hover': { color: '#533B7D', textDecoration: 'underline' }
                                }}
                              >
                                {request.user_name || 'Unknown User'}
                              </Typography>
                              <Chip
                                label={getStatusLabel(request.status)}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(request.status),
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              />
                            </Box>

                            <Typography variant="body1" sx={{ mb: 1 }}>
                              wants to join your event
                            </Typography>

                            {request.event && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EventIcon sx={{ fontSize: 18, color: '#666' }} />
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#533B7D' }}>
                                  {request.event.title}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TimeIcon sx={{ fontSize: 16, color: '#666' }} />
                              <Typography variant="body2" color="text.secondary">
                                {new Date(request.created_at).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Status Badge for larger screens */}
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Chip
                              label={getStatusLabel(request.status)}
                              sx={{
                                backgroundColor: getStatusColor(request.status),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        </Box>
                      </CardContent>

                          {/* Action Buttons */}
                      {request.status === 'SUBMITTED' && (
                        <>
                          <Divider />
                          <CardActions sx={{ 
                            p: { xs: 1.5, sm: 2 }, 
                            pt: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                          }}>
                            <Button
                              variant="contained"
                              fullWidth={isMobile}
                              startIcon={actionLoading === request.id ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                              onClick={() => handleAcceptRequest(request.id)}
                              disabled={actionLoading === request.id}
                              sx={{
                                backgroundColor: '#4caf50',
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                  backgroundColor: '#45a049'
                                }
                              }}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="outlined"
                              fullWidth={isMobile}
                              startIcon={<CloseIcon />}
                              onClick={() => handleDeclineRequest(request.id)}
                              disabled={actionLoading === request.id}
                              sx={{
                                borderColor: '#f44336',
                                color: '#f44336',
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                  borderColor: '#d32f2f',
                                  backgroundColor: 'rgba(244, 67, 54, 0.04)'
                                }
                              }}
                            >
                              Decline
                            </Button>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <InboxIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No requests in your inbox
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When people request to join your events, they'll appear here.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Chats Tab */}
        {tabValue === 1 && (
          <>
            {chatsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#533B7D' }} />
              </Box>
            ) : threads.length > 0 ? (
              <Grid container spacing={3}>
                {threads.map((thread) => {
                  const threadParticipants = participants[thread.id] || [];

                  return (
                    <Grid item xs={12} key={thread.id}>
                      <Card
                        elevation={1}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 2
                          }
                        }}
                        onClick={() => handleChatClick(thread.id)}
                      >
                        <CardContent sx={{ pb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            {/* Event Avatar */}
                            <Badge
                              badgeContent={thread.unread_count > 0 ? thread.unread_count : null}
                              color="error"
                              max={99}
                            >
                              <Avatar sx={{ 
                                backgroundColor: '#533B7D', 
                                width: { xs: 40, sm: 48 }, 
                                height: { xs: 40, sm: 48 }
                              }}>
                                <EventIcon />
                              </Avatar>
                            </Badge>

                            {/* Chat Content */}
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {thread.event?.title || 'Event Chat'}
                                </Typography>
                                <Chip
                                  label={thread.scope === 'booking' ? 'Active' : 'Pending'}
                                  size="small"
                                  sx={{
                                    backgroundColor: thread.scope === 'booking' ? '#4caf50' : '#ff9800',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </Box>

                              {/* Last message preview */}
                              {thread.last_message && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: thread.unread_count > 0 ? 'bold' : 'normal'
                                  }}
                                >
                                  {thread.last_message.kind === 'system' ? (
                                    <em>{thread.last_message.body}</em>
                                  ) : (
                                    `${thread.last_message.sender_name || 'Someone'}: ${thread.last_message.body}`
                                  )}
                                </Typography>
                              )}

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 16, color: '#666' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {thread.participant_count} participant{thread.participant_count !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TimeIcon sx={{ fontSize: 16, color: '#666' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {thread.last_message
                                      ? formatChatTime(thread.last_message.created_at)
                                      : formatChatTime(thread.created_at)
                                    }
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ChatIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No chats yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When you join events, your chats will appear here.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/chat')}
                  sx={{
                    mt: 2,
                    backgroundColor: '#533B7D',
                    '&:hover': {
                      backgroundColor: '#4a2f6b'
                    }
                  }}
                >
                  Open Chat
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Inbox;
